import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AgentToolBlock Component
 *
 * Displays an agent-as-tool execution with streaming operation fields.
 * Shows progressive updates as fields stabilize during streaming.
 */
import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CaretUpIcon, CaretDownNewIcon, } from "../../icons";
import { UserInterviewBlock } from "./UserInterviewBlock";
import { ToolCall } from "./ToolCall";
import { ToolStatusIndicator } from "./ToolStatusIndicator";
import { getToolDisplay } from "./toolRegistry";
import { ToolInputOutputPanel, hasToolInputOutputPayload } from "./ToolInputOutputPanel";
// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════
const getToolDisplayName = (name, status) => {
    const { name: label, icon } = getToolDisplay(name, status === "complete");
    return { icon, label };
};
/**
 * Check if a string looks like an ISO date
 */
const isISODateString = (str) => {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str);
};
/**
 * Check if a string looks like a file path
 */
const isFilePath = (str) => {
    return /^([A-Za-z]:\\|\/|~\/|\.\.\/)/.test(str) ||
        /\.(xlsx|xls|csv|pdf|docx|pptx|txt|json|xml|py|ts|js)$/i.test(str);
};
const formatPrimitiveValue = (value) => {
    if (value === null) {
        return { display: 'null', className: 'text-gray-400 italic', icon: '∅' };
    }
    if (value === undefined) {
        return { display: 'undefined', className: 'text-gray-400 italic', icon: '∅' };
    }
    if (typeof value === 'boolean') {
        return value
            ? { display: 'true', className: 'text-emerald-600 font-medium', icon: '✓' }
            : { display: 'false', className: 'text-red-500 font-medium', icon: '✗' };
    }
    if (typeof value === 'number') {
        // Format large numbers with locale, preserve decimals
        const formatted = Number.isInteger(value)
            ? value.toLocaleString()
            : value.toLocaleString(undefined, { maximumFractionDigits: 6 });
        return { display: formatted, className: 'text-blue-600 font-mono' };
    }
    if (typeof value === 'string') {
        // Check if it's an ISO date string
        if (isISODateString(value)) {
            try {
                const date = new Date(value);
                const formatted = date.toLocaleString();
                return { display: formatted, className: 'text-violet-600', icon: '📅' };
            }
            catch { /* not a date */ }
        }
        // Check if it's a file path
        if (isFilePath(value)) {
            // Show just the filename for long paths
            const parts = value.replace(/\\\\/g, '\\').split(/[/\\]/);
            const filename = parts[parts.length - 1];
            const display = value.length > 50 ? `.../${filename}` : value;
            return { display, className: 'text-amber-700 font-mono text-[12px]', icon: '📁' };
        }
        // Regular string - show without quotes for cleaner display
        const truncated = value.length > 100 ? `${value.slice(0, 100)}...` : value;
        return { display: truncated, className: 'text-emerald-700' };
    }
    return { display: String(value), className: 'text-gray-900' };
};
/**
 * Format a value as a collapsed summary string
 */
const formatFieldValueSummary = (value) => {
    if (value === null || value === undefined)
        return '∅ null';
    if (typeof value === 'string') {
        if (isISODateString(value)) {
            try {
                return `📅 ${new Date(value).toLocaleString()}`;
            }
            catch { /* not a date */ }
        }
        if (isFilePath(value)) {
            const parts = value.replace(/\\\\/g, '\\').split(/[/\\]/);
            return `📁 ${parts[parts.length - 1]}`;
        }
        return value.length > 60 ? `${value.slice(0, 60)}...` : value;
    }
    if (typeof value === 'boolean')
        return value ? '✓ true' : '✗ false';
    if (typeof value === 'number') {
        return Number.isInteger(value)
            ? value.toLocaleString()
            : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }
    if (Array.isArray(value)) {
        if (value.length === 0)
            return '[ ]';
        return `[${value.length} items]`;
    }
    if (typeof value === 'object') {
        const keys = Object.keys(value);
        return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '...' : ''}}`;
    }
    return String(value);
};
/**
 * Check if a value is expandable (object or non-empty array)
 */
const isExpandableValue = (value) => {
    if (Array.isArray(value))
        return value.length > 0;
    if (value !== null && typeof value === 'object')
        return Object.keys(value).length > 0;
    return false;
};
/**
 * Convert camelCase, PascalCase, or snake_case to Title Case for display
 * Handles consecutive capitals: camelCaseABC → Camel Case ABC
 * Handles snake_case: snake_case → Snake Case
 */
const formatDisplayName = (str) => {
    // Handle array notation like "requirements[0]"
    const match = str.match(/^(.+)\[(\d+)\]$/);
    if (match) {
        return `${formatDisplayName(match[1])}[${match[2]}]`;
    }
    // Handle snake_case first
    if (str.includes('_')) {
        return str.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }
    // Handle camelCase and PascalCase with consecutive capitals
    // "camelCaseABC" → "Camel Case ABC"
    return str
        // Insert space before uppercase that follows lowercase
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // Insert space between consecutive capitals when followed by lowercase
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        // Capitalize first letter
        .replace(/^./, s => s.toUpperCase())
        .trim();
};
// Legacy alias
const camelToTitleCase = formatDisplayName;
const ExpandableFieldValue = ({ value, depth = 0, maxDepth = 3, defaultExpanded = true // Expanded by default!
 }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // For primitives, use the enhanced formatter with icons
    if (!isExpandableValue(value) || depth >= maxDepth) {
        const formatted = formatPrimitiveValue(value);
        return (_jsxs("span", { className: `text-[12px] break-all inline-flex items-center gap-1 ${formatted.className}`, children: [formatted.icon && _jsx("span", { className: "opacity-70", children: formatted.icon }), formatted.display] }));
    }
    const isArray = Array.isArray(value);
    const entries = isArray
        ? value.map((v, i) => [i, v])
        : Object.entries(value);
    // Inline compact view for small objects when collapsed
    const isSmall = entries.length <= 3 && entries.every(([_, v]) => !isExpandableValue(v));
    return (_jsxs("div", { className: "flex flex-col", children: [_jsxs("button", { onClick: () => setIsExpanded(!isExpanded), className: "inline-flex items-center gap-1.5 text-[12px] hover:bg-gray-100 rounded px-1 -ml-1 transition-colors cursor-pointer", children: [_jsx("svg", { className: `w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`, fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z", clipRule: "evenodd" }) }), _jsx("span", { className: "text-gray-500", children: isArray ? `Array(${entries.length})` : `Object` })] }), isExpanded && (_jsx("div", { className: "mt-1 ml-2 bg-white rounded border border-gray-100 overflow-hidden", children: entries.map(([key, val], idx) => {
                    const isLast = idx === entries.length - 1;
                    // Format key nicely: array indices stay as [0], object keys get Title Case
                    const keyDisplay = isArray ? `[${key}]` : formatDisplayName(String(key));
                    const hasNestedExpandable = isExpandableValue(val);
                    return (_jsxs("div", { className: `flex ${!isLast ? 'border-b border-gray-50' : ''} ${hasNestedExpandable ? 'flex-col' : 'items-center'}`, children: [_jsxs("div", { className: `flex items-start gap-2 px-2 py-1 ${hasNestedExpandable ? 'w-full' : ''}`, children: [_jsx("span", { className: "text-[12px] text-gray-500 shrink-0 min-w-[60px]", children: keyDisplay }), !hasNestedExpandable && (_jsx(ExpandableFieldValue, { value: val, depth: depth + 1, maxDepth: maxDepth, defaultExpanded: depth < 1 }))] }), hasNestedExpandable && (_jsx("div", { className: "pl-4 pb-1", children: _jsx(ExpandableFieldValue, { value: val, depth: depth + 1, maxDepth: maxDepth, defaultExpanded: depth < 1 }) }))] }, `${key}-${idx}`));
                }) }))] }));
};
// ═══════════════════════════════════════════════════════════════════════════
// AGENT-SPECIFIC UI HELPERS
// ═══════════════════════════════════════════════════════════════════════════
const parseArgsObject = (args) => {
    if (!args)
        return undefined;
    try {
        return JSON.parse(args);
    }
    catch {
        return undefined;
    }
};
const toReadableLine = (value) => {
    if (typeof value === "string") {
        const text = value.trim();
        return text.length > 0 ? text : null;
    }
    if (value && typeof value === "object") {
        const candidate = value;
        const requirement = typeof candidate.requirement === "string" ? candidate.requirement.trim() : "";
        if (requirement.length > 0) {
            return requirement;
        }
        const uiDescription = typeof candidate.uiDescription === "string" ? candidate.uiDescription.trim() : "";
        if (uiDescription.length > 0)
            return uiDescription;
    }
    return null;
};
const toReadableLines = (value) => {
    if (Array.isArray(value)) {
        return value.flatMap((entry) => toReadableLines(entry));
    }
    const line = toReadableLine(value);
    return line ? [line] : [];
};
const dedupeLines = (lines) => {
    const seen = new Set();
    const output = [];
    for (const line of lines) {
        const key = line.trim().toLowerCase();
        if (!key || seen.has(key))
            continue;
        seen.add(key);
        output.push(line);
    }
    return output;
};
const extractAgentRequirementLines = (agent) => {
    const lines = [];
    const requirementFields = agent.fields.filter((field) => {
        const name = field.name.toLowerCase();
        return (name === "requirements" ||
            name.startsWith("requirements[") ||
            name === "ui_description" ||
            name === "uidescription" ||
            name === "ui_descriptions" ||
            name === "uidescriptions" ||
            name.startsWith("ui_description[") ||
            name.startsWith("uidescription[") ||
            name.startsWith("ui_descriptions[") ||
            name.startsWith("uidescriptions["));
    });
    for (const field of requirementFields) {
        lines.push(...toReadableLines(field.value));
    }
    return dedupeLines(lines);
};
const extractInlineToolDescriptions = (tool) => {
    const lines = [];
    const parsedArgs = parseArgsObject(tool.args);
    const fieldCandidates = tool.fields?.filter((field) => {
        const name = field.name.toLowerCase();
        return (name === "uidescription" ||
            name === "ui_description" ||
            name === "uidescriptions" ||
            name === "ui_descriptions" ||
            name.startsWith("uidescriptions[") ||
            name.startsWith("ui_descriptions[") ||
            name === "requirements" ||
            name.startsWith("requirements["));
    }) ?? [];
    for (const field of fieldCandidates) {
        lines.push(...toReadableLines(field.value));
    }
    if (parsedArgs) {
        lines.push(...toReadableLines(parsedArgs.uiDescription));
        lines.push(...toReadableLines(parsedArgs.ui_description));
        lines.push(...toReadableLines(parsedArgs.uiDescriptions));
        lines.push(...toReadableLines(parsedArgs.ui_descriptions));
        lines.push(...toReadableLines(parsedArgs.requirements));
    }
    return dedupeLines(lines);
};
const InlineInternalToolRow = ({ tool, isFirstAfterRequirements = false, isLastTool = false }) => {
    const isComplete = tool.status === "complete";
    const isActive = tool.status === "streaming" || tool.status === "calling";
    const parsedArgs = parseArgsObject(tool.args);
    const toolInputPayload = useMemo(() => parsedArgs ?? tool.args ?? undefined, [parsedArgs, tool.args]);
    const { name, icon } = getToolDisplay(tool.name, isComplete, parsedArgs);
    const descriptions = useMemo(() => extractInlineToolDescriptions(tool), [tool]);
    const hasInputOutput = useMemo(() => hasToolInputOutputPayload(toolInputPayload, tool.result), [toolInputPayload, tool.result]);
    const hasBody = descriptions.length > 0 || hasInputOutput;
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [didAutoCollapse, setDidAutoCollapse] = useState(false);
    const hasExpandedBody = hasBody && !isCollapsed;
    const rowBottomPaddingClass = hasExpandedBody || isLastTool ? "pb-1" : "pb-0";
    useEffect(() => {
        setIsCollapsed(false);
        setDidAutoCollapse(false);
    }, [tool.id]);
    useEffect(() => {
        let timeoutId = null;
        if (hasBody && isComplete && !didAutoCollapse) {
            timeoutId = window.setTimeout(() => {
                setIsCollapsed(true);
                setDidAutoCollapse(true);
            }, 2000);
        }
        if (isActive) {
            setIsCollapsed(false);
            setDidAutoCollapse(false);
        }
        return timeoutId !== null ? () => window.clearTimeout(timeoutId) : undefined;
    }, [didAutoCollapse, hasBody, isActive, isComplete]);
    return (_jsxs("section", { className: `flex flex-col items-start px-3 relative self-stretch w-full ${isFirstAfterRequirements ? "pt-1" : "pt-0"} ${rowBottomPaddingClass}`, children: [_jsxs("header", { className: "flex items-center justify-between px-0 py-2 relative self-stretch w-full flex-[0_0_auto] bg-white", children: [_jsxs("div", { className: "inline-flex items-center gap-2 relative flex-[0_0_auto] min-w-0", children: [_jsx("span", { className: "inline-flex items-center justify-center w-4 h-4 text-gray-600 shrink-0", children: icon }), _jsx("p", { className: "font-sans-body-helper-bold font-[number:var(--sans-body-helper-bold-font-weight)] text-gray-600 text-[length:var(--sans-body-helper-bold-font-size)] tracking-[var(--sans-body-helper-bold-letter-spacing)] leading-[var(--sans-body-helper-bold-line-height)] whitespace-nowrap [font-style:var(--sans-body-helper-bold-font-style)] truncate", children: name })] }), _jsxs("div", { className: "inline-flex items-center gap-2 relative self-stretch flex-[0_0_auto]", children: [_jsx(ToolStatusIndicator, { status: tool.status }), hasBody && (_jsx("button", { type: "button", className: "inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[3px] hover:bg-gray-100", "aria-label": isCollapsed ? "Expand tool details" : "Collapse tool details", onClick: () => setIsCollapsed((prev) => !prev), children: isCollapsed ? (_jsx(CaretDownNewIcon, { className: "w-[12px] h-[12px]" })) : (_jsx(CaretUpIcon, { className: "w-[12px] h-[12px]" })) }))] })] }), hasExpandedBody && (_jsxs("div", { className: "flex items-start pl-2 pr-0 py-1 relative self-stretch w-full flex-[0_0_auto] bg-gray-100 rounded-md", children: [_jsxs("div", { className: "flex flex-col w-4 items-center justify-center relative self-stretch", "aria-hidden": "true", children: [_jsx("div", { className: "relative w-4 h-2" }), _jsx("div", { className: "relative flex-1 w-px grow bg-gray-300 rounded-[22px]" }), _jsx("div", { className: "relative w-4 h-2" })] }), _jsxs("div", { className: "flex flex-col items-start relative flex-1 grow", children: [descriptions.map((line, index) => (_jsx("div", { className: `flex items-start gap-2 px-0 py-1 relative self-stretch w-full flex-[0_0_auto] rounded-md ${index > 0 ? "border-t border-gray-200" : ""}`, children: _jsx("div", { className: "flex items-center gap-1 relative flex-1 grow", children: _jsx("p", { className: "relative flex-1 mt-[-1.00px] font-sans-body-helper font-[number:var(--sans-body-helper-font-weight)] text-gray-900 text-[length:var(--sans-body-helper-font-size)] tracking-[var(--sans-body-helper-letter-spacing)] leading-[var(--sans-body-helper-line-height)] [font-style:var(--sans-body-helper-font-style)]", children: line }) }) }, `${tool.id}-desc-${index}`))), hasInputOutput && (_jsx(ToolInputOutputPanel, { input: toolInputPayload, output: tool.result, className: `w-full ${descriptions.length > 0 ? "pt-[4px]" : "py-[1px]"}` }))] })] }))] }));
};
// ═══════════════════════════════════════════════════════════════════════════
// HELPER: Extract UserInterviewData from tool fields
// ═══════════════════════════════════════════════════════════════════════════
const buildUserInterviewData = (tool) => {
    try {
        // Extract questions from fields
        const questionsField = tool.fields?.find(f => f.name === 'questions');
        let questions = [];
        if (questionsField && Array.isArray(questionsField.value)) {
            questions = questionsField.value;
        }
        else {
            // Try to find questions from indexed fields like questions[0], questions[1], etc.
            const questionFields = tool.fields?.filter(f => f.name.startsWith('questions[')) || [];
            questions = questionFields
                .sort((a, b) => {
                const idxA = parseInt(a.name.match(/\[(\d+)\]/)?.[1] || '0');
                const idxB = parseInt(b.name.match(/\[(\d+)\]/)?.[1] || '0');
                return idxA - idxB;
            })
                .map(f => f.value)
                .filter(q => q && typeof q === 'object');
        }
        if (questions.length === 0)
            return null;
        return {
            id: tool.id,
            questions,
            currentQuestionIndex: 0,
            answers: [],
            status: tool.status === 'complete' ? 'completed' : 'active',
        };
    }
    catch {
        return null;
    }
};
// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export const AgentToolBlock = ({ toolData }) => {
    // ─── Non-agent tools: ALWAYS render as simplified Figma card ────────
    // Only known agent-as-tool names get the full agent UI with expand/collapse,
    // thinking sections, and nested tool rendering. Everything else is a simple card.
    const KNOWN_AGENTS = new Set([
        'alphaSenseSearchAgent',
        'imageSearchAgent', 'ImageSearchAgent',
        'presentationLibrarySearchAgent', 'PresentationLibrarySearchAgent',
        'inPresentationSearchAgent', 'InPresentationSearchAgent', // legacy history
        'externalFileSearchAgent', 'ExternalFileSearchAgent',
        'linkedExcelEditorAgent',
        'editContentAgent', 'editFormatAgent', 'editChartAgent', 'editStructureAgent',
        'executionAgent', 'multiSlideBuilderAgent',
        'plannerAgent',
    ]);
    const hasAgentDetails = Boolean((toolData.segments && toolData.segments.length > 0) ||
        toolData.currentText ||
        toolData.status === 'executing');
    const isKnownAgent = KNOWN_AGENTS.has(toolData.name) || (toolData.isAgent === true && hasAgentDetails);
    if (!isKnownAgent) {
        // Convert AgentToolData status to InternalToolCall status
        const toolStatus = toolData.status === 'executing' ? 'calling'
            : toolData.status === 'starting' ? 'streaming'
                : toolData.status === 'error' ? 'stopped'
                    : toolData.status;
        const internalTool = {
            id: toolData.id,
            name: toolData.name,
            fields: toolData.fields,
            args: undefined,
            result: toolData.result,
            status: toolStatus,
            isAgent: false,
        };
        return (_jsx("div", { className: "my-2", children: _jsx(ToolCall, { tool: internalTool, segmentId: toolData.id, isExpanded: false, onToggle: () => { } }) }));
    }
    // ─── Agent tools with reasoning: full container ──────────────────────
    const [isExpanded, setIsExpanded] = useState(true); // Start expanded to show streaming
    const [expandedThinkingSections, setExpandedThinkingSections] = useState({});
    const [completedThinkingDurations, setCompletedThinkingDurations] = useState({});
    const [timerTick, setTimerTick] = useState(0); // Force re-renders for live timer display
    const thinkingStartTimesRef = React.useRef({});
    const previousActiveThinkingGroupRef = React.useRef(null);
    const { icon, label } = getToolDisplayName(toolData.name, toolData.status);
    const requirementLines = useMemo(() => extractAgentRequirementLines(toolData), [toolData]);
    const isStreaming = toolData.status === 'streaming' || toolData.status === 'starting';
    const isExecuting = toolData.status === 'executing';
    const isComplete = toolData.status === 'complete';
    const isError = toolData.status === 'error';
    const activeThinkingGroupId = useMemo(() => {
        if (!toolData.currentText)
            return null;
        return `current-text-group-${toolData.id}-${toolData.segments?.length ?? 0}`;
    }, [toolData.currentText, toolData.id, toolData.segments]);
    // Keep per-thinking-group timing stable across streaming transitions.
    useEffect(() => {
        if (!toolData.isAgent)
            return;
        const now = Date.now();
        const previousGroupId = previousActiveThinkingGroupRef.current;
        if (previousGroupId && previousGroupId !== activeThinkingGroupId) {
            const previousStart = thinkingStartTimesRef.current[previousGroupId];
            if (previousStart && completedThinkingDurations[previousGroupId] === undefined) {
                const duration = Math.max(1, Math.round((now - previousStart) / 1000));
                setCompletedThinkingDurations((prev) => ({
                    ...prev,
                    [previousGroupId]: duration,
                }));
            }
        }
        if (activeThinkingGroupId && thinkingStartTimesRef.current[activeThinkingGroupId] === undefined) {
            thinkingStartTimesRef.current[activeThinkingGroupId] = now;
        }
        previousActiveThinkingGroupRef.current = activeThinkingGroupId;
    }, [activeThinkingGroupId, completedThinkingDurations, toolData.isAgent]);
    // Force re-render every second while a thinking section is active.
    useEffect(() => {
        if (!activeThinkingGroupId)
            return undefined;
        const intervalId = window.setInterval(() => {
            setTimerTick((tick) => tick + 1);
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [activeThinkingGroupId]);
    // Auto-collapse main block when execution completes
    useEffect(() => {
        if (toolData.status === 'complete' || toolData.status === 'error' || toolData.status === 'stopped') {
            setIsExpanded(false);
        }
    }, [toolData.status]);
    // Auto-collapse thinking sections when they complete
    useEffect(() => {
        if (!toolData.currentText && !isStreaming && !isExecuting) {
            // Collapse all thinking sections
            setExpandedThinkingSections(prev => {
                const newState = { ...prev };
                Object.keys(newState).forEach(key => {
                    newState[key] = false;
                });
                return newState;
            });
        }
    }, [toolData.currentText, isStreaming, isExecuting]);
    return (_jsxs("div", { className: `my-2 rounded-lg border border-solid border-gray-300 bg-white overflow-hidden ${(isStreaming || isExecuting) ? 'shadow-[0px_0px_6px_-4px_rgba(0,0,0,0.1),0px_4px_4px_0px_rgba(0,0,0,0.06),0px_0px_2px_0px_rgba(0,0,0,0.06),0px_0px_1px_0px_rgba(0,0,0,0.04)]' : ''}`, children: [_jsxs("button", { onClick: () => setIsExpanded(!isExpanded), className: "w-full gap-1.5 px-2 py-[7px] bg-white border-b [border-bottom-style:solid] border-gray-300 flex items-center justify-center relative cursor-pointer focus:outline-none", children: [_jsx("div", { className: "flex w-6 h-6 p-0.5 bg-gray-100 rounded border border-solid border-gray-200 items-center justify-center gap-2 relative", children: _jsx("div", { className: "w-4 h-4 flex items-center justify-center text-gray-600", children: icon }) }), _jsx("p", { className: "font-[number:var(--sans-body-helper-bold-font-weight)] text-gray-600 text-[length:var(--sans-body-helper-bold-font-size)] leading-[var(--sans-body-helper-bold-line-height)] relative min-w-0 flex-1 truncate font-sans-body-helper-bold tracking-[var(--sans-body-helper-bold-letter-spacing)] whitespace-nowrap [font-style:var(--sans-body-helper-bold-font-style)] text-left", children: label }), _jsx("div", { className: "flex items-center justify-end gap-2 relative flex-1 self-stretch grow", children: _jsx("div", { className: "flex w-6 h-6 border border-solid border-transparent bg-transparent items-center justify-center relative", children: isExpanded ? (_jsx(CaretUpIcon, { className: "w-4 h-4 text-gray-700" })) : (_jsx(CaretDownNewIcon, { className: "w-4 h-4 text-gray-700" })) }) })] }), isExpanded && (() => {
                // Check if there's any content to display
                const hasContent = toolData.fields.length > 0 ||
                    (toolData.isAgent && (toolData.segments?.length || toolData.currentText)) ||
                    isComplete ||
                    isError ||
                    isExecuting;
                if (!hasContent)
                    return null;
                return (_jsxs("div", { className: "pb-1", children: [requirementLines.length > 0 && (_jsx("section", { className: "flex flex-col items-start justify-center gap-2 pt-0 pb-0 px-3 relative self-stretch w-full flex-[0_0_auto]", children: _jsx("div", { className: "flex items-start gap-2 px-0 pt-1.5 pb-0 relative self-stretch w-full flex-[0_0_auto] rounded-md", children: _jsx("div", { className: "flex flex-col items-start px-1 py-0 relative flex-1 grow", children: requirementLines.map((line, index) => (_jsxs(React.Fragment, { children: [_jsx("div", { className: `flex flex-col items-start gap-1 px-0 relative self-stretch w-full flex-[0_0_auto] ${index < requirementLines.length - 1 ? "py-1" : "pt-1 pb-[2px]"}`, children: _jsx("p", { className: "relative self-stretch mt-[-1.00px] [font-family:'Mulish-SemiBold',Helvetica] font-semibold text-gray-900 text-[13px] tracking-[0] leading-[19.5px]", children: line }) }), index < requirementLines.length - 1 && (_jsx("div", { className: "flex flex-col items-start gap-2 px-0 py-1 relative self-stretch w-full flex-[0_0_auto]", children: _jsx("div", { className: "relative self-stretch w-full h-px bg-gray-200 rounded-[22px]" }) }))] }, `${toolData.id}-requirement-${index}`))) }) }) })), toolData.isAgent && (() => {
                            const groups = [];
                            let pendingTextSegments = [];
                            const flushPendingTextSegments = () => {
                                if (pendingTextSegments.length === 0)
                                    return;
                                const firstSegmentId = pendingTextSegments[0]?.id ?? `${groups.length}`;
                                groups.push({
                                    type: 'text-group',
                                    id: `text-group-${firstSegmentId}`,
                                    textSegments: pendingTextSegments,
                                });
                                pendingTextSegments = [];
                            };
                            toolData.segments?.forEach((segment) => {
                                if (segment.type === 'text' && segment.textContent) {
                                    pendingTextSegments.push(segment);
                                    return;
                                }
                                if (segment.type === 'tool' && segment.toolData) {
                                    flushPendingTextSegments();
                                    groups.push({
                                        type: 'tool',
                                        id: segment.id,
                                        toolSegment: segment,
                                    });
                                }
                            });
                            flushPendingTextSegments();
                            if (toolData.currentText && activeThinkingGroupId) {
                                groups.push({
                                    type: 'text-group',
                                    id: activeThinkingGroupId,
                                    textSegments: [],
                                });
                            }
                            const firstGroupIndexAfterRequirements = groups.findIndex((group) => group.type === "text-group" || group.type === "tool");
                            const toolGroupIndices = groups.reduce((acc, group, index) => {
                                if (group.type === "tool") {
                                    acc.push(index);
                                }
                                return acc;
                            }, []);
                            const lastToolGroupIndex = toolGroupIndices.length > 0 ? toolGroupIndices[toolGroupIndices.length - 1] : -1;
                            return groups.map((group, groupIndex) => {
                                if (group.type === 'text-group') {
                                    const textSegments = group.textSegments ?? [];
                                    const isCurrentGroup = Boolean(activeThinkingGroupId && group.id === activeThinkingGroupId);
                                    const isThinking = Boolean(isCurrentGroup &&
                                        toolData.currentText &&
                                        (isStreaming || isExecuting));
                                    const key = `thinking-${group.id}`;
                                    const liveNow = Date.now() + timerTick * 0;
                                    let duration = completedThinkingDurations[group.id];
                                    if (isCurrentGroup) {
                                        const start = activeThinkingGroupId ? thinkingStartTimesRef.current[activeThinkingGroupId] : undefined;
                                        if (start) {
                                            duration = Math.max(1, Math.round((liveNow - start) / 1000));
                                        }
                                    }
                                    if (duration === undefined && textSegments.length > 0) {
                                        const starts = textSegments
                                            .map((segment) => segment.timestamp)
                                            .filter((ts) => typeof ts === "number");
                                        const groupStart = starts.length > 0 ? Math.min(...starts) : undefined;
                                        if (groupStart) {
                                            const firstSegmentIndex = toolData.segments?.findIndex((segment) => segment.id === textSegments[0].id) ?? -1;
                                            const nextToolTimestamp = firstSegmentIndex >= 0
                                                ? toolData.segments
                                                    ?.slice(firstSegmentIndex + textSegments.length)
                                                    .find((segment) => segment.type === "tool" && typeof segment.timestamp === "number")
                                                    ?.timestamp
                                                : undefined;
                                            if (typeof nextToolTimestamp === "number" && nextToolTimestamp >= groupStart) {
                                                duration = Math.max(1, Math.round((nextToolTimestamp - groupStart) / 1000));
                                            }
                                        }
                                    }
                                    const isSectionExpanded = expandedThinkingSections[key] !== undefined
                                        ? expandedThinkingSections[key]
                                        : isThinking;
                                    const toggleThinking = () => {
                                        setExpandedThinkingSections((prev) => ({
                                            ...prev,
                                            [key]: !isSectionExpanded,
                                        }));
                                    };
                                    const isFirstGroupAfterRequirements = requirementLines.length > 0 && groupIndex === firstGroupIndexAfterRequirements;
                                    const thoughtMonoStyle = {
                                        fontFamily: '"IBM Plex Mono", "IBM_Plex_Mono-Regular", Helvetica, monospace',
                                    };
                                    const thoughtLabel = (_jsxs("p", { className: `mt-[-1.00px] relative w-fit font-sans-note-micro text-[9px] leading-[11px] tracking-[0px] whitespace-nowrap ${isSectionExpanded ? "text-gray-600" : "text-gray-500 group-hover:text-gray-600"}`, children: [_jsx("span", { className: "font-semibold font-sans-note-micro tracking-[0px] text-[9px] leading-[11px]", children: "Thought" }), typeof duration === "number" && (_jsxs("span", { className: "font-sans-note-micro font-normal tracking-[0px] text-[9px] leading-[11px]", children: [" ", "for ", duration, "s"] }))] }));
                                    const markdownComponents = {
                                        p: ({ children }) => _jsx("p", { className: "mb-1 last:mb-0 whitespace-pre-wrap", children: children }),
                                        ul: ({ children }) => _jsx("ul", { className: "list-disc list-outside mb-1 ml-4", children: children }),
                                        ol: ({ children }) => _jsx("ol", { className: "list-decimal list-outside mb-1 ml-4", children: children }),
                                        li: ({ children }) => _jsx("li", { className: "leading-relaxed [&>p]:inline [&>p]:m-0", children: children }),
                                        code: ({ children }) => _jsx("code", { className: "bg-gray-200 px-1 py-0.5 rounded text-[10px]", children: children }),
                                        pre: ({ children }) => _jsx("pre", { className: "bg-gray-200 p-2 rounded my-1 overflow-x-auto", children: children }),
                                    };
                                    return (_jsx("section", { className: `flex items-start gap-2.5 px-3 relative self-stretch w-full ${isSectionExpanded ? "py-1" : "pb-0"} ${!isSectionExpanded && isFirstGroupAfterRequirements ? "pt-1" : !isSectionExpanded ? "pt-0" : ""}`, children: !isSectionExpanded ? (
                                        /* ── Collapsed: centered line ── [Thought for Xs ▼] ── line ── */
                                        _jsxs("button", { type: "button", onClick: toggleThinking, className: "group flex items-center justify-center relative flex-1 grow bg-white cursor-pointer focus:outline-none", "aria-expanded": false, "aria-label": "Expand thought details", children: [_jsx("div", { className: "relative flex-1 grow h-px bg-gray-200 group-hover:bg-gray-300 rounded-sm transition-colors" }), _jsxs("div", { className: "inline-flex items-center justify-center gap-1.5 px-1.5 py-0.5 relative flex-[0_0_auto] rounded border border-solid border-gray-200 group-hover:border-gray-300 bg-white group-hover:bg-gray-100 transition-colors", children: [thoughtLabel, _jsx("span", { className: "inline-flex flex-col items-center justify-center gap-1.5 relative flex-[0_0_auto] border-[0.75px] border-solid border-transparent opacity-50 bg-transparent", children: _jsx("span", { className: "inline-flex items-center justify-center gap-[1.5px] relative flex-[0_0_auto]", children: _jsx(CaretDownNewIcon, { className: "w-2.5 h-2.5 text-gray-500 group-hover:text-gray-600 transition-colors" }) }) })] }), _jsx("div", { className: "relative flex-1 grow h-px bg-gray-200 group-hover:bg-gray-300 rounded-sm transition-colors" })] })) : (
                                        /* ── Expanded: unified bordered box with header + content ── */
                                        _jsx("div", { className: "flex flex-col items-start gap-1 relative flex-1 grow rounded overflow-hidden border border-solid border-gray-200", children: _jsxs("div", { className: "flex flex-col items-start justify-center gap-1 px-2 py-1.5 relative self-stretch w-full flex-[0_0_auto] bg-gray-100 rounded-md", children: [_jsxs("div", { className: "flex items-center justify-center gap-2 relative self-stretch w-full flex-[0_0_auto]", children: [_jsx("div", { className: "relative flex-1 grow h-px bg-gray-300 rounded-sm opacity-0" }), thoughtLabel, _jsx("button", { type: "button", className: "ml-1 flex flex-col w-2 h-2 items-center justify-center gap-1 relative border-[0.5px] border-solid border-transparent aspect-[1] bg-transparent cursor-pointer focus:outline-none", onClick: toggleThinking, "aria-label": "Collapse thought", "aria-expanded": true, children: _jsx("span", { className: "inline-flex items-center justify-center gap-px relative flex-[0_0_auto]", children: _jsx(CaretUpIcon, { className: "w-2 h-2 text-gray-500" }) }) }), _jsx("div", { className: "relative flex-1 grow h-px bg-gray-300 rounded-sm opacity-0" })] }), textSegments.map((segment) => (_jsx("div", { className: "relative self-stretch font-normal text-gray-600 text-[10px] tracking-[0] leading-[12.0px] whitespace-pre-wrap", style: thoughtMonoStyle, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: markdownComponents, children: segment.textContent }) }, segment.id))), isCurrentGroup && toolData.currentText && (_jsx("div", { className: "relative self-stretch font-normal text-gray-600 text-[10px] tracking-[0] leading-[12.0px] whitespace-pre-wrap", style: thoughtMonoStyle, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: markdownComponents, children: toolData.currentText }) }))] }) })) }, group.id));
                                }
                                if (group.type === 'tool' && group.toolSegment?.toolData) {
                                    const segment = group.toolSegment;
                                    const internalTool = segment.toolData;
                                    if (internalTool.name === 'userInterview') {
                                        const interviewData = buildUserInterviewData(internalTool);
                                        if (interviewData) {
                                            return (_jsx("div", { className: "px-4", children: _jsx(UserInterviewBlock, { data: interviewData, onSubmit: (answers) => {
                                                        const event = new CustomEvent('carousel:agent-interview-submit', {
                                                            detail: {
                                                                interviewId: interviewData.id,
                                                                agentToolId: internalTool.id,
                                                                toolCallId: segment.toolData.id,
                                                                answers,
                                                            },
                                                        });
                                                        window.dispatchEvent(event);
                                                    } }) }, segment.id));
                                        }
                                    }
                                    return (_jsx(InlineInternalToolRow, { tool: internalTool, isFirstAfterRequirements: requirementLines.length > 0 && groupIndex === firstGroupIndexAfterRequirements, isLastTool: groupIndex === lastToolGroupIndex }, segment.id));
                                }
                                return null;
                            });
                        })(), isError && toolData.error && (_jsx("div", { className: "px-4 py-3", children: _jsx("div", { className: "text-sm text-red-600 bg-red-50 rounded p-2 border border-red-200", children: toolData.error }) }))] }));
            })()] }));
};
export default AgentToolBlock;
